import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import "./HelpCenter.css";
import MainLayout from "components/layout/main-layout/MainLayout";
import { titles } from "constants/titles";
import alerts from "constants/alerts";
import CustomButton from "components/widgets/custom-button/CustomButton";
import { Link, useHistory } from "react-router-dom";
import { IconTrash } from "assets/SVGs/SVGs";
import { FEEDBACK_RESET_VALUES } from "redux/auth/actionTypes";
import {
  getContactUsApi,
  getFeedBackSubType,
  getFeedBackType,
  submitFeedback,
} from "redux/auth/actions";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "utility";
import CustomSelectBox from "components/widgets/custom-select-box/CustomSelectBox";
import { FIELD_CONFIG, VALIDATION_FIELDS } from "./config";

const HelpCenter = () => {
  const [file, setFile] = useState(null);
  const [feedbackValue, setFeedbackValue] = useState("");
  const [selectedSubIssue, setSelectedSubIssue] = useState("");
  const [selectedValue, setSelectedValue] = useState("");

  // Mobile-like validation states
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [errorState, setErrorState] = useState({
    show: false,
    fieldErrors: { ...VALIDATION_FIELDS },
    fieldMessages: { ...VALIDATION_FIELDS },
  });

  const data = useSelector((store) => store?.auth?.contactUsDetails);
  const isLoading = useSelector((store) => store?.auth?.loading);
  const userData = useSelector((store) => store?.auth?.userData);
  const feedBackType = useSelector((store) => store?.auth?.feedBackType);
  const feedBackSubType = useSelector((store) => store?.auth?.subIssues);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const history = useHistory();
  const callLinkRef = useRef(null);

  // Initialize validation hook for snackbar
  const { snackbarState, dismissSnackbar, onApiResponse, handleApiResponse } =
    useAdvancedValidation();

  useEffect(() => {
    getContactUsApi(
      dispatch,
      !userData?.isGuestUser ? "accountowner" : "guestuser"
    );
    getFeedBackType(
      dispatch,
      !userData?.isGuestUser ? "accountowner" : "guestuser"
    );

    return () => {
      dispatch({
        type: FEEDBACK_RESET_VALUES,
        payload: [],
      });
      setSelectedValue("");
      setSelectedSubIssue("");
    };
  }, []);

  const clearError = (fieldName) => {
    if (fieldName) {
      setErrorState((prev) => ({
        ...prev,
        fieldErrors: {
          ...prev.fieldErrors,
          [fieldName]: false,
        },
        fieldMessages: {
          ...prev.fieldMessages,
          [fieldName]: "",
        },
      }));
    } else {
      setErrorState({
        show: false,
        fieldErrors: { ...VALIDATION_FIELDS },
        fieldMessages: { ...VALIDATION_FIELDS },
      });
    }
  };

  const handleFieldValidation = useCallback(
    (fieldName, value) => {
      if (validationTriggered) {
        let isValid = false;
        let errorMessage = "";

        if (fieldName === "feedbackValue") {
          if (isEmpty(value)) {
            isValid = false;
            errorMessage = FIELD_CONFIG.feedbackValue.message;
          } else if (value?.length < 10) {
            isValid = false;
            errorMessage = "Feedback must be at least 10 characters";
          } else {
            isValid = true;
            errorMessage = "";
          }
        } else {
          isValid = !isEmpty(value);
          errorMessage = isValid ? "" : FIELD_CONFIG[fieldName]?.message || "";
        }

        // Update field error state
        setErrorState((prev) => ({
          ...prev,
          show: true,
          fieldErrors: { ...prev.fieldErrors, [fieldName]: !isValid },
          fieldMessages: { ...prev.fieldMessages, [fieldName]: errorMessage },
        }));

        // Update snackbar visibility based on all field errors
        const updatedErrors = {
          ...errorState.fieldErrors,
          [fieldName]: !isValid,
        };
        const hasAnyErrors = Object.values(updatedErrors).some(
          (error) => error
        );

        if (hasAnyErrors && !snackbarState.show) {
          onApiResponse(alerts?.incorrect_or_missing_details, "error");
        } else if (!hasAnyErrors && snackbarState.show) {
          dismissSnackbar();
        }
      } else {
        // Clear field error when user starts typing (before validation triggered)
        if (errorState.fieldErrors?.[fieldName]) {
          clearError(fieldName);
        }
        if (snackbarState.show) {
          dismissSnackbar();
        }
      }
    },
    [
      validationTriggered,
      errorState.fieldErrors,
      snackbarState.show,
      handleApiResponse,
      dismissSnackbar,
    ]
  );

  const handleOnSubmitFeedback = async () => {
    // Set validation triggered to true (enables real-time validation)
    setValidationTriggered(true);

    // Validate all fields using if conditions (not if-else)
    let hasErrors = false;
    const fieldErrors = { ...VALIDATION_FIELDS };
    const fieldMessages = { ...VALIDATION_FIELDS };

    // Validate feedback type
    if (isEmpty(selectedValue)) {
      fieldErrors.feedbackType = true;
      fieldMessages.feedbackType = FIELD_CONFIG.feedbackType.message;
      hasErrors = true;
    }

    // Validate sub feedback type
    if (
      feedBackSubType?.length > 0 &&
      !isEmpty(selectedValue) &&
      isEmpty(selectedSubIssue)
    ) {
      fieldErrors.subFeedbackType = true;
      fieldMessages.subFeedbackType = FIELD_CONFIG.subFeedbackType.message;
      hasErrors = true;
    }

    // Validate feedback value
    if (isEmpty(feedbackValue)) {
      fieldErrors.feedbackValue = true;
      fieldMessages.feedbackValue = FIELD_CONFIG.feedbackValue.message;
      hasErrors = true;
    } else if (feedbackValue?.length < 10) {
      fieldErrors.feedbackValue = true;
      fieldMessages.feedbackValue = "Feedback must be at least 10 characters";
      hasErrors = true;
    }

    // Set all errors at once if any validation failed
    if (hasErrors) {
      setErrorState({
        show: true,
        fieldErrors,
        fieldMessages,
      });
      onApiResponse(alerts?.incorrect_or_missing_details, "error");
      return;
    }

    // If validation passes, submit the feedback
    let data = new FormData();
    data.append("issue_type_id", parseInt(selectedValue));
    data.append("description", feedbackValue);

    if (file && file?.name?.length > 0) {
      data.append("", file, file?.name);
    }
    if (selectedSubIssue) {
      data.append("issue_id", selectedSubIssue);
    }
    submitFeedback(
      dispatch,
      data,
      !userData?.isGuestUser ? "accountowner" : "guestuser",
      history,
      handleApiResponse
    );
  };

  const handleLinkClick = (event, call) => {
    event.preventDefault();
    if (call) {
      callLinkRef.current.click();
    } else {
      fileInputRef.current.click();
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e?.target;
    if (name === "feedback_desc") {
      setFeedbackValue(value);
      handleFieldValidation("feedbackValue", value);
    } else {
      setFile(files[0]);
    }
  };

  const handleDropdownSelect = (e) => {
    setSelectedValue(e?.value);
    setSelectedSubIssue("");
    handleFieldValidation("feedbackType", e?.value);
    getFeedBackSubType(
      dispatch,
      !userData?.isGuestUser ? "accountowner" : "guestuser",
      { issueType: e?.value }
    );
  };

  const handleDropdownSelectSubIssue = (e) => {
    setSelectedSubIssue(e?.value);
    handleFieldValidation("subFeedbackType", e?.value);
  };

  const getFeedBack = (feedBackType = []) => {
    const updateList = feedBackType?.map((ele) => {
      return {
        label: ele?.issue_type,
        value: ele?.issue_type_id,
        id: ele?.issue_type_id,
      };
    });
    return updateList;
  };

  const getSubFeedBackType = (feedBacksubType = []) => {
    const updateList = feedBacksubType?.map((ele) => {
      return {
        label: ele?.issue,
        value: ele?.issue_id,
        id: ele?.issue_id,
      };
    });
    return updateList;
  };

  // Check if there are any validation errors
  const hasValidationErrors = Object.values(errorState.fieldErrors).some(
    (error) => error
  );

  // Don't auto-close snackbar if there are validation errors
  const snackbarAutoHideDuration = hasValidationErrors && validationTriggered
    ? 0
    : snackbarState?.autoHideDuration;

  return (
    <>
      {/* Animated MessageSnackBar - positioned with width constraints (same as AccountHolder) */}
      {snackbarState?.show && (
        <div className="hc_snackbar_animated"        >
          <MessageSnackBar
            message={snackbarState?.message}
            isVisible={true}
            type={snackbarState?.type}
            autoHideDuration={snackbarAutoHideDuration}
            constrainWidth={false}
            onDismiss={dismissSnackbar}
          />
        </div>
      )}
      <MainLayout
        text={titles?.help_center}
        isBackHide={false}
        isSemiHeader={true}
        white={false}
        isLoading={isLoading}
      >
        <p className="display_h3 text-white-color left mb20">
          {titles?.help_center_assist_text}
        </p>
        <p className={"display_h3 text-white-color mb15 left"}>
          {titles?.email}
        </p>
        <div className="mb15">
          <p className={"display_h3 text-white-color left"}>
            {titles?.help_center_customer_service}
          </p>
          <p
            className={"text-white-color body_h1"}
          >{`Mon to Fri, ${data?.weekday_hours} ${data?.timezone}`}</p>
          <Link
            to="#"
            onClick={(e) => {
              window.location.href = `mailto:${data?.customer_care_email}`;
              e.preventDefault();
            }}
            className={"text-white-color body_h1"}
          >
            {data?.customer_care_email}
          </Link>
          {data?.weenend_hours && data?.weenend_hours?.length > 0 ? (
            <p className="text-white-color body_h1">
              {`Sat Sun, ${data?.weenend_hours} ${data?.timezone}`}
            </p>
          ) : null}
        </div>
        <div className="mb15">
          <p className={"display_h3 text-white-color left"}>
            {titles?.help_center_billing}
          </p>
          <p
            className={"text-white-color body_h1"}
          >{`Mon to Fri, ${data?.billing_service_hours_weekdays} ${data?.timezone}`}</p>
          <Link
            to="#"
            onClick={(e) => {
              window.location.href = `mailto:${data?.billing_service_email}`;
              e.preventDefault();
            }}
            className={"text-white-color body_h1"}
          >
            {data?.billing_service_email}
          </Link>
          {data?.billing_service_hours_weekends &&
            data?.billing_service_hours_weekends?.length > 0 ? (
            <p className="text-white-color body_h1">
              {`Sat Sun, ${data?.billing_service_hours_weekends} ${data?.timezone}`}
            </p>
          ) : null}
        </div>
        <div className="mg_horizontal_line"></div>
        <p className="display_h3 text-white-color mb10 left">{titles?.phone}</p>
        <CustomButton
          width={"100%"}
          text={data?.customer_care_no}
          onClick={(e) => handleLinkClick(e, true)}
        />
        <p className="display_h3 text-white-color mb10 left mt20">
          {titles?.issue_type}
        </p>
        <CustomSelectBox
          selectedValue={selectedValue}
          onChange={handleDropdownSelect}
          options={getFeedBack(feedBackType)}
          title={titles?.select_issue_type}
          error={
            validationTriggered &&
            errorState?.fieldErrors?.feedbackType &&
            errorState.show
          }
          errorMessage={
            validationTriggered &&
              errorState?.fieldErrors?.feedbackType &&
              errorState.show
              ? errorState.fieldMessages?.feedbackType
              : ""
          }
        />
        <p className="display_h3 text-white-color mb10 left mt20">{titles?.issue}</p>
        <CustomSelectBox
          disabled={feedBackSubType?.length > 0 ? false : true}
          selectedValue={selectedSubIssue}
          onChange={handleDropdownSelectSubIssue}
          options={getSubFeedBackType(feedBackSubType)}
          title={titles?.select_issue}
          error={
            validationTriggered &&
            errorState.fieldErrors?.subFeedbackType &&
            errorState.show
          }
          errorMessage={
            validationTriggered &&
              errorState.fieldErrors?.subFeedbackType &&
              errorState.show
              ? errorState.fieldMessages?.subFeedbackType
              : ""
          }
        />
        <div className="hc_desc_container mt20">
          <p className="display_h3 text-white-color mb10 left">
            {titles?.description}
          </p>
          <textarea
            onChange={handleChange}
            className={`feedback_desc body_h1 ${validationTriggered &&
              errorState.fieldErrors?.feedbackValue &&
              errorState.show
              ? "feedback_desc_error"
              : ""
              }`}
            placeholder={titles?.write_feedback}
            row={8}
            value={feedbackValue}
            name="feedback_desc"
          />
          {validationTriggered &&
            errorState.fieldErrors?.feedbackValue &&
            errorState.show && (
              <p className="feedback_error_message mb5">
                {errorState.fieldMessages?.feedbackValue}
              </p>
            )}
        </div>
        {!file ? (
          <button
            className="web_link hc_upload_doc_link"
            onClick={handleLinkClick}
            type="button"
          >
            {titles?.upload_media_and_upload}
          </button>
        ) : (
          <div className="selected_file_name">
            <p>{file?.name}</p>
            <IconTrash
              onClick={() => setFile(null)}
              className="hc_trash_icon"
            />
          </div>
        )}
        <input
          className="hc_file_input"
          type="file"
          onChange={handleChange}
          name="file"
          ref={fileInputRef}
          style={{ display: "none" }}
        />
        <CustomButton
          width={"100%"}
          className={hasValidationErrors ? "" : "mb38"}
          errorContainer={hasValidationErrors ? "mb38" : ""}
          text={titles?.submit}
          disabled={hasValidationErrors}
          showErrorMessage={validationTriggered && hasValidationErrors}
          onClick={() => handleOnSubmitFeedback()}
        />
        <a
          ref={callLinkRef}
          href={`tel:${data?.customer_care_no} alt="Call 555-555-5555"`}
        />
      </MainLayout>
    </>
  );
};

export default HelpCenter;
